import type { DirectoryFolder, DirectoryTree } from "@/api";

/**
 * 추가 파일 업로드의 **대상 폴더 선택** 순수 코어.
 *
 * 디렉터리 트리를 고를 수 있는 평탄 목록으로 펼치고, 새 폴더 경로를 조립·검증한다. 렌더·조회·
 * 업로드는 소유하지 않는다(`ExtraFileUploadDialog`).
 *
 * **extra 폴더는 저장되지 않는다.** 서버는 파일의 `relative_path` 에서 `parent_path` 를 파생해
 * 트리를 집계한다(`service/dataset/directory_tree.py`). 그래서 여기서 만든 새 폴더는 그 폴더에
 * 첫 파일을 올릴 때 실제로 생긴다. 빈 폴더를 미리 만들 방법은 없다.
 */

// 대상 후보 한 줄. `depth` 는 들여쓰기용이고 `path` 가 실제 값(루트는 빈 문자열)이다.
export type UploadTargetOption = {
  path: string;
  name: string;
  depth: number;
  // 아직 서버에 없는 폴더(사용자가 이번 업로드에서 만든 것).
  pending: boolean;
};

export const UPLOAD_TARGET_ROOT_LABEL = "(루트)";

/**
 * 트리를 대상 후보 목록으로 펼친다(루트가 항상 첫 줄).
 *
 * 어느 폴더든 고를 수 있다. 채널·meta 같은 원본 폴더도 고를 수 있다. 원본 보호는 폴더 단위가
 * 아니라 **경로** 단위다.
 * - 서버가 Dataset 파일·schema 소유 경로를 409 로 거부한다.
 * - 다이얼로그는 고른 이름을 그 폴더에 미리 조회해(`filename` probe) 충돌 행을 막는다.
 *
 * `pendingPath` 는 사용자가 이번 다이얼로그에서 만든 새 폴더다. 서버 트리에 없으므로 여기서
 * 목록에 추가한다. 조상 폴더도 함께 만들어 중간 단계도 목록에 보이게 한다.
 */
export function uploadTargetOptions(
  tree: DirectoryTree | null,
  pendingPath: string | null = null,
): readonly UploadTargetOption[] {
  const options: UploadTargetOption[] = [
    { path: "", name: UPLOAD_TARGET_ROOT_LABEL, depth: 0, pending: false },
  ];

  const walk = (folders: readonly DirectoryFolder[], depth: number): void => {
    for (const folder of folders) {
      options.push({ path: folder.path, name: folder.name, depth, pending: false });
      walk(folder.folders, depth + 1);
    }
  };
  walk(tree?.folders ?? [], 1);

  return pendingPath === null ? options : withPendingPath(options, pendingPath);
}

// 새 폴더와 그 조상 중 목록에 없는 것만 부모 바로 뒤에 추가한다.
function withPendingPath(
  options: readonly UploadTargetOption[],
  pendingPath: string,
): readonly UploadTargetOption[] {
  const merged = [...options];
  const segments = pendingPath.split("/");
  for (let index = 0; index < segments.length; index += 1) {
    const path = segments.slice(0, index + 1).join("/");
    if (merged.some((option) => option.path === path)) {
      continue;
    }
    const parent = segments.slice(0, index).join("/");
    const parentIndex = merged.findIndex((option) => option.path === parent);
    merged.splice(parentIndex + 1, 0, {
      path,
      name: segments[index] ?? path,
      depth: index + 1,
      pending: true,
    });
  }
  return merged;
}

// 대상 경로의 표시 문자열. 루트는 경로가 빈 문자열이므로 그대로 두면 보이지 않는다.
export function formatUploadTarget(path: string): string {
  return path === "" ? UPLOAD_TARGET_ROOT_LABEL : path;
}

export type NewFolderResult = { ok: true; path: string } | { ok: false; reason: string };

/**
 * `parent` 아래에 새 폴더 경로를 만든다. 검증은 서버 규칙(`_normalize_extra_file_path`)과 같다.
 * - 빈 세그먼트는 허용하지 않는다.
 * - `.` 로 시작하는 hidden 세그먼트는 허용하지 않는다(`.`·`..` 포함).
 * - 중첩 입력(`a/b`)은 받는다.
 * - 입력한 이름은 유니코드 NFC 로 바꾼다. 붙여 넣은 macOS 폴더 이름은 NFD 일 수 있다. 서버는 본문에
 *   NFC 가 아닌 문자열이 있으면 요청을 422 로 거부한다. `parent` 는 바꾸지 않는다.
 *
 * 최종 판정은 서버가 한다(길이·중복·FilePattern 충돌). 여기서는 서버가 확실히 거부할 입력만 미리
 * 거른다.
 */
export function buildNewFolderPath(parent: string, input: string): NewFolderResult {
  const trimmed = input.trim().normalize("NFC");
  if (trimmed === "") {
    return { ok: false, reason: "폴더 이름을 입력해 주세요." };
  }
  if (trimmed.includes("\\")) {
    return { ok: false, reason: "폴더 이름에 역슬래시(\\)는 쓸 수 없습니다. 구분자는 / 입니다." };
  }
  const segments = trimmed.split("/");
  for (const segment of segments) {
    if (segment.trim() === "") {
      return { ok: false, reason: "폴더 이름에 빈 단계가 있습니다." };
    }
    if (segment.startsWith(".")) {
      return { ok: false, reason: `.으로 시작하는 폴더 이름은 쓸 수 없습니다: ${segment}` };
    }
  }
  const normalized = segments.map((segment) => segment.trim()).join("/");
  return { ok: true, path: parent === "" ? normalized : `${parent}/${normalized}` };
}
